import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { showErrorToast } from '@hooks/showErrorToast';
import { request } from '@utils';

export interface Session {
  _id: string;
  startedAt: Date;
  lockedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ActiveSessionResponse {
  session: Session | null;
  duration: number;
  requests: number;
}

export const useStartSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<Session> => {
      return await request({
        path: 'admin/sessions/start',
        method: 'GET',
        authenticate: true,
      });
    },
    onSuccess: () => {
      toast.success('Logging session started successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-active-session'] });
      queryClient.invalidateQueries({ queryKey: ['admin-requests'] });
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to start logging session');
    },
  });
};

export const useActiveSession = () => {
  return useQuery({
    queryKey: ['admin-active-session'],
    queryFn: async (): Promise<ActiveSessionResponse> => {
      try {
        const response = await request({
          path: 'admin/sessions/active',
          method: 'GET',
          authenticate: true,
        });
        return response || { session: null, duration: 0, requests: 0 };
      } catch (error) {
        showErrorToast(error, 'Failed to fetch active session');
        return { session: null, duration: 0, requests: 0 };
      }
    },
    refetchInterval: 30 * 1000, // Refetch every 30 seconds to keep session status updated
  });
};
